import React, { useState, useEffect, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import './Chatbot.css';
import ChatMessage from './ChatMessage';

// Defining API URL based on the environment (local or production)
const API_URL =
    window.location.hostname === "localhost" || window.location.hostname.startsWith("192.168.")
        ? "http://127.0.0.1:80"  // Use HTTP locally for testing
        : "https://cloud-hpc-navigator.onrender.com";

// How long to wait for a backend response before giving up and showing an
// error instead of leaving "Typing..." spinning forever. Generous enough
// for a normal LLM response, but short enough that a stalled connection
// (bad wifi, backend hiccup) doesn't leave the chat looking frozen.
const REQUEST_TIMEOUT_MS = 25000;

// Starter questions shown before the user's first real message, so a first-
// time visitor has something to click instead of facing a blank input box.
// Chosen for variety across content types (general advice, technical docs,
// case studies) - the Lyft and Moody's Analytics questions also happen to
// showcase both branches of the source-citation system: Lyft's citation
// resolves to an archived Wayback snapshot (shows the "archived" badge),
// while Moody's Analytics was confirmed to still work live and is excluded
// from the Wayback fallback entirely (shows a normal citation link).
const EXAMPLE_QUESTIONS = [
    "Why should I use HPC on the cloud and not an on premise HPC solution instead?",
    "What is the purpose of an Amazon EC2 Hpc6a instance?",
    "Has Lyft used AWS or Azure for its HPC needs?",
    "How does Moody's Analytics use Azure for HPC risk modeling?",
];

function Chatbot() {
    // State hook to manage the query entered by the user
    const [query, setQuery] = useState('');

    // State hook to manage the user ID 
    // Checks if the user's ID is present in sessionStorage and generates one if none is present
    const [userId, setUserId] = useState(() => {
        let storedUserId = sessionStorage.getItem("user_id");
        if (!storedUserId) {
            storedUserId = uuidv4();
            sessionStorage.setItem("user_id", storedUserId);
        }
        return storedUserId;
    });

    // State hook to manage the conversation history
    // Checks if conversation history is present in sessionStorage and uses default greeting message if none is present
    const [conversation, setConversation] = useState(() => {
        const storedConversation = sessionStorage.getItem("conversation");
        return storedConversation
            ? JSON.parse(storedConversation)
            : [{ role: "assistant", content: "Hi! I'm an assistant that helps with high-performance computing (HPC) on AWS and Azure. How can I assist you today?" }];
    });

    // Effect hook to save the conversation history in sessionStorage whenever it changes
    useEffect(() => {
        sessionStorage.setItem("conversation", JSON.stringify(conversation));
    }, [conversation]);

    // State hook to manage the loading state of the chatbot
    const [loading, setLoading] = useState(false);

    // Ref to the message input, used to return keyboard focus there after a
    // message is sent or an example-question chip is clicked (the chip
    // itself disappears once the conversation starts, which would otherwise
    // strand keyboard/screen-reader focus with nothing obvious to land on).
    const inputRef = useRef(null);

    // Effect hook to log when the chatbot is initialized
    useEffect(() => {
        console.log("🟢 Chatbot initialized with system message.");
    }, []);

    // Handle input change in the query field
    const handleInputChange = (event) => {
        setQuery(event.target.value);
    };

    // Sends a message to the backend API and updates the conversation.
    // Takes the query text as a parameter (rather than reading the `query`
    // state directly) so it can be called both from the form's submit
    // handler and from an example-question chip click without hitting a
    // stale-closure issue (setQuery + an immediate submit in the same tick
    // would otherwise still see the old, pre-update query value).
    const sendMessage = async (queryText) => {
        if (!queryText.trim()) return;

        const newConversation = [...conversation, { role: "user", content: queryText }];
        setConversation(newConversation);
        setQuery('');
        setLoading(true);

        // Abort the request if the backend never responds, instead of
        // leaving "Typing..." spinning forever with no explanation - a bad
        // look if this happens live in front of an interviewer.
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

        try {
            const res = await fetch(`${API_URL}/chat`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({user_id: userId, query: queryText, conversation: newConversation}),
                signal: controller.signal
            });

            const data = await res.json();

            if (data.conversation) {
                console.log("✅ Backend Response:", data.conversation);
                setConversation(data.conversation);
            }
            else {
                console.error("Error:", data.error);
                setConversation([...newConversation, {
                    role: "assistant",
                    content: "Sorry, something went wrong processing that on my end. Please try asking again."
                }]);
            }

            if (data.user_id) {
                setUserId(data.user_id)
            }

            else {
                console.error("Error:", data.error);
            }
        }

        catch (error) {
            // Covers network failures (backend unreachable, offline) and the
            // timeout above (fetch throws an AbortError when the signal
            // fires). Either way, surface something in the chat itself
            // rather than only logging to a console no one in an interview
            // is looking at.
            const isTimeout = error.name === 'AbortError';
            console.error(isTimeout ? "Request timed out" : "Failed to reach the chatbot backend", error);
            setConversation([...newConversation, {
                role: "assistant",
                content: isTimeout
                    ? "Sorry, that's taking longer than expected. Please try again in a moment."
                    : "Sorry, I'm having trouble connecting right now. Please check your connection and try again."
            }]);
        }

        finally {
            clearTimeout(timeoutId);
            setLoading(false);
            inputRef.current?.focus();
        }
    };

    // Handling form submission (the input box + Send button)
    const handleSubmit = (event) => {
        event.preventDefault();
        sendMessage(query);
    };

    // Handles a click on an example-question chip: submits that question
    // immediately, same as typing it in and hitting Send.
    const handleExampleClick = (question) => {
        sendMessage(question);
    };

    // Handles reseting the chat history
    const handleClear = async () => {
        // Clear whatever's currently typed in the input too - "start a new
        // conversation" should mean a full reset, not leave a half-typed
        // question sitting in the box from the previous conversation. This
        // is local UI state, so it's cleared unconditionally rather than
        // only on a successful backend response.
        setQuery('');
        inputRef.current?.focus();

        try {
            const res = await fetch(`${API_URL}/clear`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                // Send userId state value with the key "user_id" to match the backend
                body: JSON.stringify({ user_id: userId })
            });
            const data = await res.json();
            if (data.conversation) {
                setConversation(data.conversation);
                sessionStorage.setItem("conversation", JSON.stringify(data.conversation));
            } else {
                console.error("Error clearing conversation:", data.error);
            }
        } catch (error) {
            console.error("Failed to clear conversation", error);
        }
    };
    

    return (
        <div className="chatbot-container">
            <div className="header">
                <button onClick={handleClear} className="clear-button" disabled={loading}>
                    Start a new conversation!
                </button>
            </div>
            {/* role="log" + aria-live="polite" makes this a live region: a
                screen reader announces each new message (the bot's reply,
                and the transient "Typing..." status) as it's added, instead
                of staying silent until the user happens to re-focus this
                area. aria-relevant="additions" keeps it from re-announcing
                the whole history on every render. */}
            <div
                className="messages-list"
                role="log"
                aria-live="polite"
                aria-relevant="additions"
                aria-label="Conversation with the chatbot"
            >
                {conversation.map((msg, index) => (
                    <ChatMessage key={index} message={msg} />
                ))}
                {loading && <div className="chat-message bot typing">Typing...</div>}
            </div>
            {/* Only shown before the user's first real message (just the
                default greeting present) - disappears once a conversation
                actually starts, so returning users aren't shown clutter. */}
            {conversation.length === 1 && !loading && (
                <div className="example-questions">
                    {EXAMPLE_QUESTIONS.map((question) => (
                        <button
                            key={question}
                            type="button"
                            className="example-question-chip"
                            onClick={() => handleExampleClick(question)}
                        >
                            {question}
                        </button>
                    ))}
                </div>
            )}
            <form className="message-form" onSubmit={handleSubmit}>
                <label htmlFor="chat-query-input" className="sr-only">
                    Ask a question about HPC on AWS or Azure
                </label>
                <input
                    id="chat-query-input"
                    ref={inputRef}
                    type="text"
                    value={query}
                    onChange={handleInputChange}
                    placeholder="Ask a question..."
                    className="message-input"
                    disabled={loading}
                />
                <button type="submit" className="send-button" disabled={loading}>Send</button>
            </form>
        </div>
    );
    
}

export default Chatbot;
